/**
 * Feedback Model
 *
 * This file defines the data model for feedback in the system.
 */

export interface FeedbackModel {
  id: string;
  notes: string;
  status: "NEW" | "REVIEWED";
  date: string;
  grade: string | { id: string; name: string };
  isActive: boolean;
  type: "SUBMISSION_FEEDBACK" | "QUERY_FEEDBACK";
  student: { id: string; name: string; regno: string };
  teacher: { id: string; name: string };
  lesson?: { id: string; name: string };
  unit?: { id: string; name: string };
  query_id?: string;
  assignment: {
    id: string;
    name: string;
    assignment_type: string;
    grade: { id: string; name: string };
    unit: { id: string; name: string };
    lesson: { id: string; name: string };
  };
  created_date: string;
}

export type QueryRecordType = "course" | "class";

export interface FeedbackQuery {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: string;
  isActive?: boolean;
  fromDate?: string | Date;
  toDate?: string | Date;
  gradeId?: string;
  classId?: string;
  recordType?: QueryRecordType;
  studentId?: string;
  teacherId?: string;
  schoolId?: string;
  lessonId?: string;
  unitId?: string;
}

export interface QueryThread {
  type: "QUERY" | "QUERY_FEEDBACK";
  id: string;
  notes: string;
  creator: {
    name: string;
    role: "STUDENT" | "TEACHER";
    id: string;
  };
  created_on: string;
  grade: {
    id: string;
    name: string;
  };
  lesson: {
    id: string;
    name: string;
  };
  unit: {
    id: string;
    name: string;
  };
  uploads: {
    file_type: string | undefined;
    id: string;
    url: string;
  }[];
}

export interface QueryDetail {
  is_closed: boolean;
  id?: string;
  status?: "NEW" | "REVIEWED" | "PENDING";
  threads: QueryThread[];
}

export interface FeedbackFormData {
  submission?: string;
  grade?: string;
  query?: string;
  is_close_assignment?: boolean;
  /** `class` when submitting from class offering view */
  type?: QueryRecordType;
  notes: string;
  unit?: string;
  lesson?: string;
  uploads: string[];
}

/** grade holds grade id (course) or class offering id (class) */
export function buildQueryAddPayload(params: {
  recordType: QueryRecordType;
  resourceId: string;
  assignmentType: string;
  notes: string;
  uploads: string[];
  lessonId?: string;
  unitId?: string;
}): FeedbackFormData {
  const isUnit = params.assignmentType === "unit";
  const payload: FeedbackFormData = {
    notes: params.notes,
    uploads: params.uploads,
    grade: params.resourceId,
  };

  if (params.recordType === "class") {
    payload.type = "class";
    if (isUnit) {
      payload.lesson = params.lessonId || "";
      payload.unit = params.unitId || "";
    }
    return payload;
  }

  payload.lesson = params.lessonId || "";
  payload.unit = params.unitId || "";
  return payload;
}

export interface DownloadFeedbackQuery
  extends Omit<FeedbackQuery, "page" | "pageSize"> {}
